import { formatPesoAmount } from '../utils/money';
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import usePendingPurchases from '../hooks/usePendingPurchases';
import { deletePendingPurchase } from '../api';
import CancelPendingSaleModal from './CancelPendingSaleModal';
import DocumentTabs from './DocumentTabs';
import { notify } from '../utils/alertBus';
import {
  applyActivePendingPurchaseLiveState,
  filterPendingByRegisterScope,
} from '../utils/pendingPurchases';

const PendingSalesTabs = ({
  branchId,
  activePurchaseId,
  onSelect,
  cashRegisterId,
  cashRegisterSessionId,
  registerlessProfileId,
  maxOpenSales,
  enableRealtime = true,
  tone = 'neutral',
  canDelete = true,
  canCreate = true,
  canSelect = true,
  scopeReady = true,
  activePurchaseLiveId = null,
  activePurchaseLiveTotal = null,
  activePurchaseLiveClientId = null,
  activePurchaseLiveClientName = null,
  activePurchaseLiveClientReady = true,
}) => {
  const pendingScopeParams = useMemo(() => ({
    scope: 'pos',
    ...(cashRegisterSessionId ? { cashRegisterSessionId } : {}),
    ...(registerlessProfileId ? { registerlessProfileId } : {}),
  }), [cashRegisterSessionId, registerlessProfileId]);
  const { pending, loading, error, reload } = usePendingPurchases(branchId, {
    enableRealtime,
    params: pendingScopeParams,
  });
  const [cancelTarget, setCancelTarget] = useState(null);

  const handleSelect = (idOrNull, purchase = null) => {
    if (idOrNull && !canSelect) return;
    if (!idOrNull && !canCreate) return;
    if (typeof onSelect === 'function') {
      onSelect(idOrNull, purchase);
    }
  };

  const handleDelete = (purchase) => {
    if (!canDelete) return;
    if (!branchId || !purchase?.id) return;
    setCancelTarget(purchase);
  };

  const handleConfirmDelete = async (cancelPayload) => {
    if (!canDelete) return;
    if (!branchId || !cancelTarget?.id) {
      throw new Error('No se pudo identificar la venta.');
    }
    try {
      await deletePendingPurchase(branchId, cancelTarget.id, cancelPayload || {});
      if (cancelTarget.id === activePurchaseId) {
        handleSelect(null);
      }
      await reload();
    } catch (err) {
      console.error('Error deleting pending purchase:', err);
      throw new Error(
        err?.response?.data?.message || 'No se pudo eliminar la venta pendiente.'
      );
    }
  };

  const formatLabel = (purchase) => {
    const clientName = purchase.saleName || purchase.Client?.name || 'Sin cliente';
    const total =
      typeof purchase.totalAmount === 'number'
        ? `$${formatPesoAmount(purchase.totalAmount)}`
        : '';

    let time = '';
    if (purchase.createdAt) {
      const created = new Date(purchase.createdAt);
      if (!Number.isNaN(created.getTime())) {
        time = format(created, 'HH:mm');
      }
    }

    return [clientName, total || null, time || null]
      .filter(Boolean)
      .join(' · ');
  };

  const isRegisterlessSuspendedSale = (purchase) =>
    purchase?.saleStatus === 'suspended' && purchase?.registerlessProfileId != null;

  const formatAmount = (purchase) =>
    typeof purchase?.totalAmount === 'number'
      ? `$${formatPesoAmount(purchase.totalAmount)}`
      : '';

  const formatMenuTitle = (purchase) => {
    const label = purchase?.saleName || purchase?.Client?.name || 'Sin cliente';
    return `#${purchase?.id} · ${label}`;
  };

  const formatMenuMeta = (purchase) => {
    const amount = formatAmount(purchase);
    if (isRegisterlessSuspendedSale(purchase)) {
      return ['Enviada a caja', amount || null].filter(Boolean).join(' · ');
    }
    return amount;
  };

  const isNewActive = !activePurchaseId;
  const parsedMaxOpenSales = Number.isInteger(maxOpenSales) ? maxOpenSales : 5;
  const limitApplies =
    (cashRegisterSessionId !== null && cashRegisterSessionId !== undefined)
    || (cashRegisterId !== null && cashRegisterId !== undefined);
  const pendingByStatus = useMemo(
    () =>
      (pending || []).filter((purchase) =>
        purchase?.saleStatus === 'open' || purchase?.saleStatus === 'suspended'
      ),
    [pending]
  );

  const scopedPending = useMemo(
    () => {
      if (!scopeReady) return [];
      return filterPendingByRegisterScope(pendingByStatus, {
        cashRegisterSessionId,
        cashRegisterId,
        registerlessProfileId,
      });
    },
    [pendingByStatus, cashRegisterSessionId, cashRegisterId, registerlessProfileId, scopeReady]
  );
  const displayPending = useMemo(
    () => applyActivePendingPurchaseLiveState(scopedPending, {
      purchaseId: activePurchaseLiveId,
      totalAmount: activePurchaseLiveTotal,
      clientId: activePurchaseLiveClientReady ? activePurchaseLiveClientId : undefined,
      clientName: activePurchaseLiveClientReady ? activePurchaseLiveClientName : undefined,
    }),
    [
      activePurchaseLiveClientId,
      activePurchaseLiveClientName,
      activePurchaseLiveClientReady,
      activePurchaseLiveId,
      activePurchaseLiveTotal,
      scopedPending,
    ]
  );
  const tabPending = useMemo(
    () => displayPending.filter((purchase) => !isRegisterlessSuspendedSale(purchase)),
    [displayPending]
  );
  const hasRegisterlessMenuItems = displayPending.some(isRegisterlessSuspendedSale);
  const isRegisterlessOnlyScope = !cashRegisterSessionId && Boolean(registerlessProfileId);
  const openSales = displayPending.filter((purchase) => purchase.saleStatus === 'open');
  const shouldBlockNewSale =
    limitApplies && !isNewActive && openSales.length >= parsedMaxOpenSales;
  const newSaleBlockedMessage = `Se alcanzó el límite de ventas pendientes (${parsedMaxOpenSales}) para esta caja.`;

  const handleNewSaleClick = () => {
    if (shouldBlockNewSale) {
      notify(newSaleBlockedMessage);
      return;
    }
    handleSelect(null);
  };

  const buildTabTitle = (purchase) => {
    return `#${purchase.id}`;
  };

  return (
    <>
      <DocumentTabs
        tabs={tabPending}
        menuTabs={displayPending}
        activeId={activePurchaseId}
        activeNew={isNewActive}
        onSelect={(purchase) => handleSelect(purchase.id, purchase)}
        onNew={handleNewSaleClick}
        onDelete={handleDelete}
        canCreate={canCreate}
        canSelect={canSelect}
        canDelete={canDelete}
        loading={loading}
        error={error}
        scopeReady={scopeReady}
        scopeLoadingLabel="Cargando pendientes"
        tone={tone}
        newTitle="Nueva venta"
        newDisabled={shouldBlockNewSale}
        newDisabledMessage={newSaleBlockedMessage}
        listTitle="Ver pendientes"
        menuTitle={
          hasRegisterlessMenuItems
            ? (isRegisterlessOnlyScope ? 'Ventas enviadas a caja' : 'Pendientes y ventas enviadas a caja')
            : 'Pendientes'
        }
        emptyMessage="No hay pendientes hoy."
        getTabTitle={buildTabTitle}
        getTabMeta={formatLabel}
        getMenuTabTitle={formatMenuTitle}
        getMenuTabMeta={formatMenuMeta}
      />
      <CancelPendingSaleModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        saleLabel={cancelTarget ? buildTabTitle(cancelTarget) : ''}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default PendingSalesTabs;
